/**
 * GrowthBook A/B Testing Integration
 * Shared utilities for initializing and tracking experiments across all games
 */

import { GrowthBook } from '@growthbook/growthbook-react';
import { reportEvent } from '../Analytics/Analytics';
import { EVENT_CATEGORIES, ENVIRONMENTS } from '../Analytics/AnalyticsConstants';
import { getEnvironment } from '../Analytics/EnvironmentUtils';
import { logger } from '../Analytics/Logger';

// Singleton GrowthBook instance
let growthbookInstance: GrowthBook | null = null;
let featuresLoadedPromise: Promise<void> | null = null;

/**
 * Get or create a stable anonymous user ID for experiment bucketing
 * Stored in localStorage to ensure consistent variant assignment
 */
const getUserId = (): string => {
  const STORAGE_KEY = 'gb_user_id';
  
  if (typeof window === 'undefined') {
    return 'server-side-user';
  }
  
  let userId = localStorage.getItem(STORAGE_KEY);
  
  if (!userId) {
    // Generate a random user ID
    userId = `user_${Math.random().toString(36).substring(2, 15)}${Date.now().toString(36)}`;
    localStorage.setItem(STORAGE_KEY, userId);
  }
  
  return userId;
};

/**
 * Initialize GrowthBook instance for A/B testing
 * Integrates with existing GA4 analytics via reportEvent
 * Reads client key from VITE_GROWTHBOOK_CLIENT_KEY environment variable
 * 
 * @returns Configured GrowthBook instance
 */
export const initializeGrowthBook = (): GrowthBook => {
  if (growthbookInstance) {
    logger.warn('[GrowthBook] Already initialized, returning existing instance');
    return growthbookInstance;
  }

  // Read client key from environment variable
  // @ts-ignore - import.meta.env is only available in Vite build context
  const clientKey = typeof import.meta !== 'undefined' && import.meta.env 
    ? import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY 
    : undefined;

  logger.debug('[GrowthBook] Initializing with clientKey:', clientKey ? `${clientKey.substring(0, 10)}...` : 'MISSING');
  logger.debug('[GrowthBook] Environment:', getEnvironment());
  logger.debug('[GrowthBook] User ID:', getUserId());

  const gb = new GrowthBook({
    clientKey,
    enableDevMode: getEnvironment() === ENVIRONMENTS.DEVELOPMENT,
    
    // Set user attributes for experiment bucketing
    attributes: {
      id: getUserId(), // Generate or retrieve a stable user ID
      environment: getEnvironment(),
    },
    
    // Track experiment exposure to GA4
    trackingCallback: (experiment, result) => {
      logger.debug('[GrowthBook] Experiment viewed:', {
        experimentKey: experiment.key,
        variant: result.key,
        variationId: result.variationId,
      });
      
      reportEvent({
        action: 'experiment_viewed',
        category: EVENT_CATEGORIES.GENERAL,
        label: `${experiment.key}:${result.key}`,
        value: result.variationId,
      });
      
      // Set experiment variant as user property for retention analysis
      // This allows GA4 to segment retention cohorts by experiment bucket
      if (typeof window !== 'undefined' && window.gtag) {
        const propertyName = `experiment_${experiment.key.replace(/-/g, '_')}`;
        window.gtag('set', 'user_properties', {
          [propertyName]: result.value
        });
        logger.debug(`[GrowthBook] Set user property: ${propertyName} = ${result.value}`);
      }
    },
  });

  // Load features from GrowthBook API and store the promise
  logger.debug('[GrowthBook] Loading features from API');
  featuresLoadedPromise = gb.loadFeatures({ timeout: 5000 })
    .then(() => {
      const features = gb.getFeatures();
      logger.debug('[GrowthBook] Features loaded successfully. Count:', Object.keys(features).length);
      logger.debug('[GrowthBook] Available features:', Object.keys(features));
    })
    .catch((error) => {
      logger.warn('[GrowthBook] Failed to load features (will use defaults):', error.message);
      // Don't block the app - just use default values
    });

  growthbookInstance = gb;
  return gb;
};

/**
 * Wait for GrowthBook features to load
 * Auto-initializes if not already initialized
 * @returns Promise that resolves when features are loaded
 */
export const waitForGrowthBookReady = async (): Promise<void> => {
  if (!growthbookInstance) {
    logger.debug('[GrowthBook] Auto-initializing for waitForGrowthBookReady');
    initializeGrowthBook();
  }
  
  if (featuresLoadedPromise) {
    await featuresLoadedPromise;
  }
};

/**
 * Get the singleton GrowthBook instance
 * Auto-initializes if not already initialized
 */
export const getGrowthBook = (): GrowthBook => {
  if (!growthbookInstance) {
    logger.debug('[GrowthBook] Auto-initializing on first access');
    initializeGrowthBook();
  }
  return growthbookInstance!;
};

/**
 * Get a feature value from GrowthBook with type safety
 * Waits for features to load before returning value
 * Uses the singleton instance
 * 
 * @param featureKey - Feature key to look up
 * @param defaultValue - Default value if feature not found
 * @returns Promise resolving to feature value or default
 */
export async function getFeatureValue<T>(
  featureKey: string,
  defaultValue: T
): Promise<T> {
  await waitForGrowthBookReady();
  const gb = getGrowthBook();
  const value = gb.getFeatureValue(featureKey, defaultValue) as T;
  
  // Enhanced logging to debug feature values
  const allFeatures = gb.getFeatures();
  const featureExists = featureKey in allFeatures;
  logger.debug(`[GrowthBook] getFeatureValue('${featureKey}') = ${value}`);
  logger.debug(`[GrowthBook] Feature exists in loaded features: ${featureExists}`);
  if (!featureExists) {
    logger.warn(`[GrowthBook] Feature '${featureKey}' not found. Available features:`, Object.keys(allFeatures));
  } else {
    logger.debug(`[GrowthBook] Feature '${featureKey}' definition:`, allFeatures[featureKey]);
  }
  
  return value;
}

/**
 * Email modal variant enum for A/B testing
 * Used to control when/how the email signup modal is displayed
 */
export enum EmailModalVariant {
  /** Never show the email modal */
  Never = 'never',
  /** Show email modal when page loads */
  OnPageLoad = 'on_page_load',
  /** Show email modal after completing daily puzzles */
  OnDailyPuzzlesCompletion = 'on_daily_puzzles_completion'
}

/**
 * Shared experiment name for email signup modal across all games
 */
export const EMAIL_MODAL_EXPERIMENT = 'email-sign-up';

/**
 * Get the email modal variant for the shared email signup experiment
 * Waits for features to load before returning variant
 * Used across all games (Tile Clicker, Word Drift, etc.)
 * 
 * @returns Promise resolving to current email modal variant
 */
export async function getEmailModalVariant(): Promise<EmailModalVariant> {
  let result = await getFeatureValue(EMAIL_MODAL_EXPERIMENT, EmailModalVariant.Never);

  // Manually disable this for now to avoid annoying user.
  // TODO: Re-enable when we have a better email modal experience, or once
  // we recover access to the GrowthBook account to truly disable this experience.
  if (result === EmailModalVariant.OnPageLoad) {
    logger.warn('[GrowthBook] Email modal variant is set to OnPageLoad, but it is currently disabled. Defaulting to Never.');
    return EmailModalVariant.Never;
  }
  return result;
}
